'use client';

import { useState, ChangeEvent, MouseEvent, useEffect } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { isCheckInteger } from '@/lib/BigNumbers';
import { compareObjects } from '@/lib/Compare';
import { readCommonSettingList } from '@/api/bet/read';
import { ResReadCommonSettingType } from '@/types/bet/read-type';
import { updateConfigSetting } from '@/api/bet/update';
import { UpdateConfigSettingType } from '@/types/bet/update-type';
import { createConfigSetting, deleteConfigSetting } from '@/api/bet/create';
import { CreateConfigSettingType } from '@/types/bet/create-type';
import Header from '../Header';

export default function CommonSettingForm() {
  // 초기 상태에서도 임시 ID를 생성하여 수정 가능하도록 함
  const getTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const [rows, setRows] = useState<ResReadCommonSettingType[]>([
    {
      configId: getTempId(),
      configKey: '',
      configValue: '',
      description: '',
    },
  ]);
  // 서버에서 호출 한 뒤 값을 저장 (rows, oldRows)
  const [oldRows, setOldRows] = useState<ResReadCommonSettingType[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const re = await readCommonSettingList();
        // 서버에서 받은 데이터는 그대로 사용 (configId가 이미 있음)
        // 빈 배열이거나 데이터가 없을 때만 초기 행 추가
        if (re.data && re.data.length > 0) {
          // null 값을 빈 문자열로 변환하여 React 경고 방지
          const normalizedData = re.data.map((item: ResReadCommonSettingType) => ({
            ...item,
            configKey: item.configKey ?? '',
            configValue: item.configValue ?? '',
            description: item.description ?? '',
          }));
          setRows(normalizedData);
          setOldRows(normalizedData);
        } else {
          // 데이터가 없을 때만 초기 행 추가
          const initialRow = {
            configId: getTempId(),
            configKey: '',
            configValue: '',
            description: '',
          };
          setRows([initialRow]);
          setOldRows([initialRow]);
        }
      } catch (error) {
        console.error('공통 세팅 목록 조회 실패:', error);
        // 에러 발생 시에도 초기 행은 유지
      }
    })();
  }, []);

  const handleAddInputs = () => {
    // 새 행에는 임시 ID를 생성 (서버에 저장되기 전까지 사용)
    // temp- 접두사로 서버에서 생성된 ID와 구분
    const tempId = getTempId();
    setRows([
      ...rows,
      {
        configId: tempId,
        configKey: '',
        configValue: '',
        description: '',
      },
    ]);
  };

  const handleDelete = () => {
    if (rows.length > 1) {
      setRows((prevRows) => prevRows.slice(0, -1));
    } else {
      toast.error('최소 1개의 행이 필요합니다.');
    }
  };

  const handleInputChange = (rowId: string, field: keyof ResReadCommonSettingType, value: string) => {
    if (!rowId || rowId === '') {
      console.error('rowId가 없습니다:', rowId);
      return;
    }

    setRows((prevRows) => {
      // rowId로 특정 행 찾기
      const targetRow = prevRows.find((row) => row.configId === rowId);
      if (!targetRow) {
        console.error('해당 rowId를 가진 행을 찾을 수 없습니다:', rowId, prevRows);
        return prevRows;
      }

      // 숫자 필드 검증 (configValue 필드)
      if (field === 'configValue') {
        if (value !== '' && !isCheckInteger(value)) {
          toast.error('소수점을 사용할 수 없습니다.');
          return prevRows;
        }
        // configValue는 마이너스 값 방지
        if (value !== '') {
          const numValue = Number(value);
          if (numValue < 0) {
            toast.error('마이너스 값을 입력할 수 없습니다.');
            return prevRows;
          }
        }
      }

      // 특정 행의 특정 필드만 업데이트 (엄격한 비교)
      return prevRows.map((row) => {
        if (row.configId === rowId) {
          return { ...row, [field]: value };
        }
        return row;
      });
    });
  };

  const handleUpdate = async (event: MouseEvent<HTMLButtonElement>) => {
    console.log('공통 세팅 업데이트');
    console.log('현재 rows:', rows);
    console.log('oldRows:', oldRows);

    try {
      // 1단계: DELETE - oldRows에는 있지만 rows에는 없는 행 삭제
      const deletedRows = oldRows.filter(
        (oldRow) => !oldRow.configId.startsWith('temp-') && !rows.find((row) => row.configId === oldRow.configId),
      );
      console.log('DELETE 대상 행:', deletedRows);

      const deletePromises = deletedRows.map(async (row) => {
        console.log('행 삭제:', row.configId);
        return await deleteConfigSetting(row.configId);
      });

      // 2단계: CREATE - temp-로 시작하는 ID는 새로 생성
      const createRows = rows.filter((row) => row.configId.startsWith('temp-'));
      console.log('CREATE 대상 행:', createRows);

      const createPromises = createRows.map(async (row) => {
        const createData: CreateConfigSettingType = {
          configKey: row.configKey,
          configValue: row.configValue,
          description: row.description,
        };
        console.log('새 행 생성:', createData);
        return await createConfigSetting(createData);
      });

      // 3단계: UPDATE - 기존 행의 변경사항 업데이트
      const updatePromises = rows
        .map((row) => {
          // temp-로 시작하는 행은 제외 (이미 create에서 처리)
          if (row.configId.startsWith('temp-')) {
            return null;
          }

          const oldRow = oldRows.find((old) => old.configId === row.configId);
          if (!oldRow) {
            return null;
          }

          // 변경사항 확인
          const changes = compareObjects(row, oldRow);

          // 변경사항이 있으면 업데이트
          if (Object.keys(changes).length > 0) {
            const updateData: UpdateConfigSettingType = {
              configId: row.configId,
              ...changes,
            };
            console.log('업데이트 데이터:', updateData);
            return updateConfigSetting(updateData);
          }

          return null;
        })
        .filter((promise) => promise !== null) as Promise<any>[];

      // DELETE, CREATE, UPDATE를 순차적으로 실행
      const deleteResults = await Promise.all(deletePromises);
      console.log('삭제 결과:', deleteResults);

      const createResults = await Promise.all(createPromises);
      console.log('생성 결과:', createResults);

      const updateResults = await Promise.all(updatePromises);
      console.log('업데이트 결과:', updateResults);

      toast.success('저장이 완료되었습니다.');

      // 저장 후 서버에서 최신 데이터 다시 가져오기
      const re = await readCommonSettingList();
      if (re.data && re.data.length > 0) {
        // null 값을 빈 문자열로 변환하여 React 경고 방지
        const normalizedData = re.data.map((item: ResReadCommonSettingType) => ({
          ...item,
          configKey: item.configKey ?? '',
          configValue: item.configValue ?? '',
          description: item.description ?? '',
        }));
        setRows(normalizedData);
        setOldRows(normalizedData);
      } else {
        // 데이터가 없을 때만 초기 행 추가
        const initialRow = {
          configId: getTempId(),
          configKey: '',
          configValue: '',
          description: '',
        };
        setRows([initialRow]);
        setOldRows([initialRow]);
      }
    } catch (error) {
      console.error('저장 실패:', error);
      toast.error('저장에 실패했습니다.');
    }
  };

  return (
    <Card>
      <Header title="공통 세팅" />
      <Divider />
      <CardContent
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        <Box p={2}>
          <Grid xs={12}>
            <Typography variant="h4">공통 세팅</Typography>
          </Grid>
          <Grid container spacing={2} mb={2}>
            <Grid xs={4} sm={4}>
              <Typography variant="h6">이름</Typography>
            </Grid>
            <Grid xs={4} sm={4}>
              <Typography variant="h6">값</Typography>
            </Grid>
            <Grid xs={4} sm={4}>
              <Typography variant="h6">설명</Typography>
            </Grid>
          </Grid>

          {rows.map((row: ResReadCommonSettingType, index: number) => {
            // 각 행은 이미 configId를 가지고 있어야 함 (useEffect에서 처리)
            const rowId = row.configId;

            return (
              <Grid container spacing={2} key={rowId || `row-${index}`} mb={2}>
                <Grid xs={4} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>이름</InputLabel>
                    <OutlinedInput
                      value={row.configKey ?? ''}
                      label="이름"
                      name="configKey"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInputChange(row.configId, 'configKey', e.target.value);
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid xs={4} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>값</InputLabel>
                    <OutlinedInput
                      value={row.configValue ?? ''}
                      inputProps={{
                        type: 'number',
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        min: 0,
                      }}
                      placeholder="값"
                      label="값"
                      name="configValue"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInputChange(row.configId, 'configValue', e.target.value);
                      }}
                    />
                  </FormControl>
                  <FormHelperText>※ 숫자만 입력 가능</FormHelperText>
                </Grid>

                <Grid xs={4} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>설명</InputLabel>
                    <OutlinedInput
                      value={row.description ?? ''}
                      label="설명"
                      name="description"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        handleInputChange(row.configId, 'description', e.target.value);
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            );
          })}

          <Grid container spacing={2} mt={2}>
            <Grid xs={6}>
              <Button fullWidth variant="outlined" color="secondary" onClick={handleAddInputs}>
                추가
              </Button>
            </Grid>
            <Grid xs={6}>
              <Button fullWidth variant="outlined" color="secondary" onClick={handleDelete}>
                삭제
              </Button>
            </Grid>
            <Grid xs={12}>
              <Button fullWidth variant="contained" color="primary" onClick={handleUpdate}>
                공통 세팅 업데이트
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}
